import React, { useEffect, useMemo, useCallback } from "react";
import { Card, Button, Space, Table, Popconfirm, Tag } from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "Store/hooks";
import { setActiveKey } from "Layout/redux/slice";
import { setLoadingState } from "Components/Basic/full_page_loader/redux/slice";
import { setErrorState } from "Components/Basic/ErrorBoundary/redux/slice";
import {
  getPaddleProduct,
  getProductPrices,
  deletePaddlePrice,
} from "../redux/thunks";
import {
  selectCurrentPaddleProduct,
  selectIsPaddleProductLoading,
} from "../redux/selectors";
import { IPaddlePrice } from "../redux/types";
import { ColumnsType } from "antd/es/table";
import "./ProductPricesPage.index.css";

const ProductPricesPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { productId } = useParams<{ productId: string }>();
  const currentProduct = useAppSelector(selectCurrentPaddleProduct);
  const isLoading = useAppSelector(selectIsPaddleProductLoading);

  useEffect(() => {
    const fetchData = async () => {
      if (!productId) return;

      try {
        dispatch(setLoadingState(true));
        dispatch(setActiveKey(["paddle-products"]));
        await dispatch(getPaddleProduct(productId)).unwrap();
        await dispatch(getProductPrices(productId)).unwrap();
      } catch (err: any) {
        dispatch(
          setErrorState({
            message: "Error",
            description: err.message || "Failed to load product prices",
            placement: "topRight",
            type: "error",
          })
        );
      } finally {
        dispatch(setLoadingState(false));
      }
    };

    fetchData();
  }, [dispatch, productId]);

  const handleBack = () => {
    navigate("/payment-products");
  };

  const handleAddPrice = () => {
    navigate(`/payment-products/${productId}/add-price`);
  };

  const handleEditPrice = useCallback((priceId: string) => {
    navigate(`/payment-products/${productId}/edit-price/${priceId}`);
  }, [navigate, productId]);

  const handleDeletePrice = useCallback(async (priceId: string) => {
    if (!productId) return;

    try {
      await dispatch(deletePaddlePrice({ productId, priceId })).unwrap();
      dispatch(
        setErrorState({
          message: "Success",
          description: "Price deleted successfully",
          placement: "topRight",
          type: "success",
        })
      );
      // Refresh prices list
      await dispatch(getProductPrices(productId)).unwrap();
    } catch (error: any) {
      dispatch(
        setErrorState({
          message: "Error",
          description: error.message || "Failed to delete price",
          placement: "topRight",
          type: "error",
        })
      );
    }
  }, [dispatch, productId]);

  const priceColumns: ColumnsType<IPaddlePrice> = useMemo(
    () => [
      {
        title: "Type",
        dataIndex: "priceType",
        key: "priceType",
        width: 120,
        render: (type: string) => (
          <Tag color={type === "subscription" ? "blue" : "orange"}>
            {type?.charAt(0).toUpperCase() + type?.slice(1)}
          </Tag>
        ),
      },
      {
        title: "Billing Interval",
        dataIndex: "billingInterval",
        key: "billingInterval",
        width: 150,
        render: (interval: string) => (
          <span style={{ textTransform: "capitalize" }}>{interval}</span>
        ),
      },
      {
        title: "Price Name",
        dataIndex: "priceName",
        key: "priceName",
        width: 150,
        render: (_: any, price: IPaddlePrice) => {
          const priceName = price.customData?.price_name as string;
          return priceName ? (
            <span>{priceName}</span>
          ) : (
            <span style={{ color: "#999", fontStyle: "italic" }}>No name</span>
          );
        },
      },

      {
        title: "Price",
        key: "price",
        width: 150,
        render: (_: any, price: IPaddlePrice) => {
          const formattedPrice = (price.priceInCents / 100).toFixed(2);
          return `${price.currency} ${formattedPrice}`;
        },
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        ellipsis: true,
      },
      {
        title: "Status",
        dataIndex: "isActive",
        key: "isActive",
        width: 100,
        render: (isActive: boolean) => (
          <Tag color={isActive ? "green" : "red"}>
            {isActive ? "Active" : "Inactive"}
          </Tag>
        ),
      },
      {
        title: "Price ID",
        dataIndex: "priceId",
        key: "priceId",
        width: 200,
        ellipsis: true,
        render: (id: string) => (
          <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
            {id}
          </span>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 220,
        render: (_: any, price: IPaddlePrice) => (
          <Space size="middle">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditPrice(price.priceId)}
              title="Edit Price"
            >
              Edit
            </Button>
            <Popconfirm
              title="Delete Price"
              description="Are you sure you want to delete this price? This action cannot be undone."
              onConfirm={() => handleDeletePrice(price.priceId)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                title="Delete Price"
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [handleEditPrice, handleDeletePrice]
  );

  const prices = currentProduct?.prices || [];

  return (
    <div className="product-prices-page-container">
      <Card className="product-prices-page-card">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          className="back-button"
        >
          Back to Products
        </Button>

        <div className="product-prices-page-header">
          <div className="product-prices-page-title">
            <h2>Prices for {currentProduct?.name || "Product"}</h2>
            {currentProduct?.description && (
              <p className="product-description">
                {currentProduct.description}
              </p>
            )}
          </div>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddPrice}
          >
            Add Price
          </Button>
        </div>
        <div className="table-scroll-container">
          {isLoading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <div>Loading prices...</div>
            </div>
          ) : prices.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <p>No prices configured for this product.</p>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddPrice}
                style={{ marginTop: "16px" }}
              >
                Add First Price
              </Button>
            </div>
          ) : (
            <Table
              columns={priceColumns}
              dataSource={prices}
              rowKey="priceId"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total: number, range: [number, number]) =>
                  `${range[0]}-${range[1]} of ${total} prices`,
              }}
              scroll={{ x: 1400, y: "calc(100vh - 345px)" }}
              size="small"
              bordered
            />
          )}
        </div>
      </Card>
    </div>
  );
};

export default ProductPricesPage;
